import type { AudioProcessor } from "@twilio/voice-sdk";

type Compression = {
  threshold: number;
  knee: number;
  ratio: number;
  attack: number;
  release: number;
};

type Frequency = {
  highPassFilter: number;
  lowPassFilter: number;
};

export type AudioProcessorOptions = {
  gain: number;
  compressionStart: Compression;
  compressionFinish: Compression;
  frequency: Frequency;
};

export class AudioBoostProcessor implements AudioProcessor {
  public audioContext: AudioContext | null;
  public options: AudioProcessorOptions;

  private background: MediaElementAudioSourceNode;
  private destination: MediaStreamAudioDestinationNode;
  private inputAnalyser: AnalyserNode;
  private outputAnalyser: AnalyserNode;
  private gainNode: GainNode;
  private limiterNode: DynamicsCompressorNode;
  private compressorNode: DynamicsCompressorNode;
  private highPassFilter: BiquadFilterNode;
  private lowPassFilter: BiquadFilterNode;
  private fftSize: number = 256;

  constructor(options: AudioProcessorOptions) {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    this.options = options;
    console.log(
      "%cAudioBoostProcessor",
      "color:red;background:#222;padding:5px;",
      JSON.stringify(this.options, null, 2),
    );
  }

  public setGainLevel(level: number) {
    if (this.gainNode && this.audioContext) {
      this.gainNode.gain.setValueAtTime(level, this.audioContext.currentTime);
      this.options.gain = level;
      console.log("%cGain updated", "color:cyan", level);
    }
  }

  async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
    const source = this.audioContext.createMediaStreamSource(stream);
    this.inputAnalyser = this.audioContext.createAnalyser();
    this.outputAnalyser = this.audioContext.createAnalyser();
    this.inputAnalyser.fftSize = this.fftSize;
    this.outputAnalyser.fftSize = this.fftSize;

    // Compressor Node
    this.compressorNode = this.audioContext.createDynamicsCompressor();
    Object.entries(this.options.compressionStart).forEach(([key, value]) => {
      try {
        this.compressorNode[key].setValueAtTime(value, this.audioContext.currentTime);
      } catch (e) {
        console.error("compressionStart Error", { e });
      }
    });

    // Gain node
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.options.gain;

    // High-pass & Low-pass filters
    this.highPassFilter = this.audioContext.createBiquadFilter();
    this.highPassFilter.type = "highpass";
    this.highPassFilter.frequency.value = this.options.frequency.highPassFilter;

    this.lowPassFilter = this.audioContext.createBiquadFilter();
    this.lowPassFilter.type = "lowpass";
    this.lowPassFilter.frequency.value = this.options.frequency.lowPassFilter;

    this.limiterNode = this.audioContext.createDynamicsCompressor();
    Object.entries(this.options.compressionFinish).forEach(([key, value]) => {
      try {
        this.limiterNode[key].setValueAtTime(value, this.audioContext.currentTime);
      } catch (e) {
        console.error("compressionFinish Error", { e });
      }
    });

    // Destination node
    this.destination = this.audioContext.createMediaStreamDestination();

    // Connect the full chain
    source
      .connect(this.highPassFilter)
      .connect(this.lowPassFilter)
      .connect(this.inputAnalyser)
      .connect(this.compressorNode)
      .connect(this.gainNode)
      .connect(this.limiterNode)
      .connect(this.outputAnalyser)
      .connect(this.destination);

    return this.destination.stream;
  }

  async destroyProcessedStream(): Promise<void> {
    this.background?.disconnect();
    this.destination?.disconnect();
    this.highPassFilter?.disconnect();
    this.lowPassFilter?.disconnect();
    this.inputAnalyser?.disconnect();
    this.compressorNode?.disconnect();
    this.gainNode?.disconnect();
    this.limiterNode?.disconnect();
    this.outputAnalyser?.disconnect();
  }
}
